import { z } from 'zod';
import { idSchema, localDateSchema, recordLinkSchema, urlSchema } from './common';

/**
 * Wiedza i pliki (docs/3VTOS_MVP.md §8, Figma 10 Wiedza): `knowledge_items`, `knowledge_versions`, `files`
 * (packages/db/src/schema/knowledge.ts). Moduł uprawnień: `knowledge`.
 *
 * Rekord wiedzy ma zakres (firma / klient / projekt), rodzaj (notatka, decyzja, link, plik), autora, źródło,
 * datę i wersję. Treść Markdown żyje w `knowledge_versions` (każda edycja = nowa wersja, `current_version`
 * w rekordzie); baza jest źródłem prawdy, CLIENT.md i PROJECT.md to generowany widok.
 * Dostęp dziedziczy po kliencie albo projekcie (PERMISSIONS §3.2): odebranie projektu zabiera jego wiedzę i pliki.
 * Wejścia i wyjścia usług dopisuje agent modułu knowledge.
 */

export const KNOWLEDGE_SCOPES = ['workspace', 'client', 'project'] as const;
export type KnowledgeScope = (typeof KNOWLEDGE_SCOPES)[number];
export const knowledgeScopeSchema = z.enum(KNOWLEDGE_SCOPES);

export const KNOWLEDGE_KINDS = ['note', 'decision', 'link', 'file'] as const;
export type KnowledgeKind = (typeof KNOWLEDGE_KINDS)[number];
export const knowledgeKindSchema = z.enum(KNOWLEDGE_KINDS);

/** Źródło rekordu wiedzy (link w `source_ref`: { type, id }). */
export const KNOWLEDGE_SOURCE_TYPES = ['manual', 'meeting', 'message', 'agent_run', 'offer', 'service'] as const;
export type KnowledgeSourceType = (typeof KNOWLEDGE_SOURCE_TYPES)[number];
export const knowledgeSourceTypeSchema = z.enum(KNOWLEDGE_SOURCE_TYPES);

export const knowledgeTitleSchema = z
  .string()
  .trim()
  .min(1, { message: 'Tytuł jest wymagany' })
  .max(300, { message: 'Najwyżej 300 znaków' });

/** Treść notatki (Markdown). Większy limit niż zwykłe opisy. */
export const knowledgeBodySchema = z.string().max(200_000, { message: 'Treść jest za długa' });

const knowledgeItemFieldsBase = z.object({
  scope: knowledgeScopeSchema,
  clientId: idSchema.nullable().optional(),
  projectId: idSchema.nullable().optional(),
  kind: knowledgeKindSchema,
  title: knowledgeTitleSchema,
  bodyMd: knowledgeBodySchema.default(''),
  url: urlSchema.nullable().optional(),
  fileId: idSchema.nullable().optional(),
  sourceType: knowledgeSourceTypeSchema.default('manual'),
  sourceRef: recordLinkSchema.nullable().optional(),
  /** data decyzji (rodzaj „decyzja”), lokalna */
  decidedOn: localDateSchema.nullable().optional(),
});

/** Spójność zakresu i rodzaju (tak samo pilnuje baza: CHECK `knowledge_items_scope_check`, `_link_check`, `_file_check`). */
export function knowledgeItemRefinement(
  v: {
    scope?: KnowledgeScope;
    clientId?: string | null;
    projectId?: string | null;
    kind?: KnowledgeKind;
    url?: string | null;
    fileId?: string | null;
  },
  ctx: z.RefinementCtx,
): void {
  if (v.scope === 'workspace' && (v.clientId || v.projectId)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scope'], message: 'Wiedza firmy nie ma klienta ani projektu' });
  }
  if (v.scope === 'client' && !v.clientId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['clientId'], message: 'Wybierz klienta' });
  }
  if (v.scope === 'client' && v.projectId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['projectId'], message: 'Wiedza klienta nie ma projektu' });
  }
  if (v.scope === 'project' && !v.projectId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['projectId'], message: 'Wybierz projekt' });
  }
  if (v.kind === 'link' && !v.url) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['url'], message: 'Podaj adres linku' });
  }
  if (v.kind === 'file' && !v.fileId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['fileId'], message: 'Dodaj plik' });
  }
}

export const knowledgeItemFieldsShape = knowledgeItemFieldsBase.shape;
export const knowledgeItemFieldsSchema = knowledgeItemFieldsBase.superRefine(knowledgeItemRefinement);
export type KnowledgeItemFields = z.infer<typeof knowledgeItemFieldsSchema>;

// ── Pliki ───────────────────────────────────────────────────────────────────

/**
 * Do czego plik jest przypięty (link polimorficzny, bez FK). `client` (DECISIONS 2026-10-17 [P] „Dokumenty klienta:
 * pliki, linki, ustalenia”): dokument klienta z zakładki „Dokumenty” — `owner_id` = `client_id`, moduł Klienci.
 */
export const FILE_OWNER_TYPES = ['knowledge_item', 'meeting', 'offer', 'artifact', 'message', 'client'] as const;
export type FileOwnerType = (typeof FILE_OWNER_TYPES)[number];
export const fileOwnerTypeSchema = z.enum(FILE_OWNER_TYPES);

/**
 * Rodzaj dokumentu klienta (`files.category`, tylko pliki z właścicielem `client`). „Umowa” i „Oferta” zawierają
 * zwykle kwoty — widzi je (i wgrywa) tylko osoba z „Widzi stawki i kwoty” (`seesRates`).
 */
export const CLIENT_DOCUMENT_CATEGORIES = ['contract', 'offer', 'client_materials', 'other'] as const;
export type ClientDocumentCategory = (typeof CLIENT_DOCUMENT_CATEGORIES)[number];
export const clientDocumentCategorySchema = z.enum(CLIENT_DOCUMENT_CATEGORIES);

export const CLIENT_DOCUMENT_CATEGORY_LABELS: Record<ClientDocumentCategory, string> = {
  contract: 'Umowa',
  offer: 'Oferta',
  client_materials: 'Materiały od klienta',
  other: 'Inne',
};

/** Rodzaje dokumentów wymagające „Widzi stawki i kwoty” (podgląd, pobranie, wgranie, archiwum). */
export const RATE_GATED_DOCUMENT_CATEGORIES: readonly ClientDocumentCategory[] = ['contract', 'offer'];

/** Czy osoba (z flagą `seesRates` albo bez) może widzieć dokument danego rodzaju. */
export function canSeeDocumentCategory(category: string | null | undefined, seesRates: boolean): boolean {
  if (seesRates || !category) return true;
  return !(RATE_GATED_DOCUMENT_CATEGORIES as readonly string[]).includes(category);
}

/** Rodzaje dokumentów do wyboru przy wgraniu (bez „Widzi stawki i kwoty”: bez Umowy i Oferty). */
export function documentCategoriesFor(seesRates: boolean): ClientDocumentCategory[] {
  return CLIENT_DOCUMENT_CATEGORIES.filter((c) => canSeeDocumentCategory(c, seesRates));
}

/** Krótki opis pliku (np. „Umowa ramowa na 2026”), najwyżej 300 znaków. */
export const FILE_DESCRIPTION_MAX = 300;
export const fileDescriptionSchema = z
  .string()
  .trim()
  .max(FILE_DESCRIPTION_MAX, { message: `Najwyżej ${FILE_DESCRIPTION_MAX} znaków` });

/** Skan pliku: czeka, czysty, zainfekowany (pobranie zablokowane), pominięty (brak skanera w dev). */
export const FILE_SCAN_STATUSES = ['pending', 'clean', 'infected', 'skipped'] as const;
export type FileScanStatus = (typeof FILE_SCAN_STATUSES)[number];
export const fileScanStatusSchema = z.enum(FILE_SCAN_STATUSES);

/** Największy przesyłany plik (25 MB). */
export const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

export const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/, { message: 'Nieprawidłowy skrót SHA-256' });

export const filenameSchema = z
  .string()
  .trim()
  .min(1, { message: 'Nazwa pliku jest wymagana' })
  .max(255, { message: 'Najwyżej 255 znaków' })
  .refine((v) => !/[\\/\0]/.test(v), { message: 'Nazwa pliku nie może zawierać ukośników' });

export const mimeSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9][a-z0-9!#$&^_.+-]*\/[a-z0-9][a-z0-9!#$&^_.+-]*$/, { message: 'Nieprawidłowy typ pliku' });

/** Metadane pliku (zapisuje usługa po wgraniu do magazynu; `storage_key` z `buildStorageKey`). */
export const fileFieldsSchema = z.object({
  filename: filenameSchema,
  mime: mimeSchema,
  sizeBytes: z.number().int().nonnegative().max(MAX_FILE_SIZE_BYTES, { message: 'Plik jest za duży (najwyżej 25 MB)' }),
  sha256: sha256Schema,
  clientId: idSchema.nullable().optional(),
  projectId: idSchema.nullable().optional(),
  ownerType: fileOwnerTypeSchema.nullable().optional(),
  ownerId: idSchema.nullable().optional(),
});
export type FileFields = z.infer<typeof fileFieldsSchema>;

// ── Dozwolone typy plików (sprawdzane po treści, nie tylko po rozszerzeniu) ─
/**
 * Rozszerzenie → typ MIME i etykieta. Usługa wgrania (packages/core/src/modules/files) rozpoznaje typ po
 * pierwszych bajtach (magic bytes); rozszerzenie musi pasować do treści (np. .exe przemianowany na .pdf = odrzucony).
 */
export const FILE_TYPES = {
  pdf: { mime: 'application/pdf', label: 'PDF' },
  png: { mime: 'image/png', label: 'PNG' },
  jpg: { mime: 'image/jpeg', label: 'JPG' },
  jpeg: { mime: 'image/jpeg', label: 'JPG' },
  webp: { mime: 'image/webp', label: 'WebP' },
  txt: { mime: 'text/plain', label: 'TXT' },
  md: { mime: 'text/markdown', label: 'Markdown' },
  csv: { mime: 'text/csv', label: 'CSV' },
  docx: { mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', label: 'Word' },
  xlsx: { mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', label: 'Excel' },
  zip: { mime: 'application/zip', label: 'ZIP' },
} as const;
export type FileExtension = keyof typeof FILE_TYPES;
export const ALLOWED_FILE_EXTENSIONS = Object.keys(FILE_TYPES) as FileExtension[];

/** Wartość atrybutu `accept` pola wyboru pliku. */
export const FILE_ACCEPT = ALLOWED_FILE_EXTENSIONS.map((e) => `.${e}`).join(',');

export const FILE_MESSAGES = {
  notAllowed: 'Ten typ pliku nie jest dozwolony. Dozwolone: PDF, PNG, JPG, WebP, TXT, MD, CSV, DOCX, XLSX, ZIP.',
  mismatch: 'Treść pliku nie pasuje do jego rozszerzenia.',
  tooLarge: 'Plik jest za duży (najwyżej 25 MB).',
  empty: 'Plik jest pusty.',
  missing: 'Wybierz plik.',
  infected: 'Plik jest zablokowany przez skaner.',
  storageUnavailable: 'Magazyn plików jest niedostępny. Spróbuj ponownie później.',
  ownerMismatch: 'Plik należy do innego klienta albo projektu.',
  categoryNeedsRates: 'Umowy i oferty widzą tylko osoby z uprawnieniem „Widzi stawki i kwoty”.',
  categoryOnlyClient: 'Rodzaj dokumentu mają tylko pliki klienta.',
  clientArchived: 'Klient jest w archiwum. Przywróć go, aby dodać plik.',
  projectRequired: 'Wybierz projekt — masz dostęp tylko do wybranych projektów tego klienta.',
  projectOtherClient: 'Projekt należy do innego klienta.',
} as const;

/** Rozszerzenie (małe litery, bez kropki) albo null. */
export function fileExtensionOf(filename: string): string | null {
  const m = /\.([A-Za-z0-9]{1,10})$/.exec(filename.trim());
  return m?.[1] ? m[1].toLowerCase() : null;
}

/** Etykieta typu pliku do list („PDF · 19.09”). */
export function fileTypeLabel(filename: string, mime: string): string {
  const ext = fileExtensionOf(filename);
  if (ext && ext in FILE_TYPES) return FILE_TYPES[ext as FileExtension].label;
  const byMime = Object.values(FILE_TYPES).find((t) => t.mime === mime);
  return byMime?.label ?? 'Plik';
}

/** Adres pobrania pliku (REST, sesja + zakres rekordu właściciela). */
export function fileDownloadPath(fileId: string): string {
  return `/api/files/${fileId}`;
}

// ── Odnośniki do źródeł (MVP §16 Etap 3: „źródło decyzji można otworzyć”) ───
/** Typy rekordów w `source_ref` (link `{ type, id }`). */
export const KNOWLEDGE_SOURCE_REF_TYPES = [
  'knowledge_item',
  'meeting',
  'message',
  'agent_run',
  'offer',
  'service',
  'task',
] as const;
export type KnowledgeSourceRefType = (typeof KNOWLEDGE_SOURCE_REF_TYPES)[number];

export const knowledgeSourceRefSchema = z.object({ type: z.enum(KNOWLEDGE_SOURCE_REF_TYPES), id: idSchema }).strict();

/**
 * Ścieżka SPA źródła. Jedno miejsce dla tras modułów (spotkania, oferty, agenci, poczta): zmiana trasy = zmiana tutaj.
 * Usługa (`service`) nie ma własnej strony — prowadzi do profilu klienta (`clientId`).
 */
export function knowledgeSourcePath(type: string, id: string, extra: { clientId?: string | null } = {}): string | null {
  switch (type) {
    case 'knowledge_item':
      return `/wiedza/${id}`;
    case 'meeting':
      return `/spotkania/${id}`;
    case 'offer':
      return `/oferty/${id}`;
    case 'agent_run':
      return `/agenci/${id}`;
    case 'message':
      return `/inbox/${id}`;
    case 'task':
      return `/zadania/${id}`;
    case 'service':
      return extra.clientId ? `/klienci/${extra.clientId}` : null;
    default:
      return null;
  }
}

// ── Wejścia usług (moduł knowledge, router `knowledge`) ─────────────────────
export const listKnowledgeInput = z
  .object({
    scope: knowledgeScopeSchema.optional(),
    clientId: idSchema.optional(),
    projectId: idSchema.optional(),
    kind: knowledgeKindSchema.optional(),
    /** tytuł i treść bieżącej wersji (bez wielkości liter i polskich znaków) */
    q: z.string().trim().max(100, { message: 'Najwyżej 100 znaków' }).optional(),
    /** true = tylko archiwum */
    archived: z.boolean().default(false),
    limit: z.number().int().min(1).max(500).default(300),
  })
  .strict()
  .default({});
export type ListKnowledgeInput = z.input<typeof listKnowledgeInput>;

export const knowledgeIdInput = z.object({ id: idSchema }).strict();

/** Nowy rekord wiedzy. Projekt wyznacza klienta (usługa uzupełnia `client_id`). Decyzja bez daty = dziś. */
export const createKnowledgeInput = z
  .object({
    ...knowledgeItemFieldsShape,
    sourceRef: knowledgeSourceRefSchema.nullable().optional(),
  })
  .strict()
  .superRefine(knowledgeItemRefinement);
export type CreateKnowledgeInput = z.input<typeof createKnowledgeInput>;

/** Edycja: zmiana treści = nowa wersja (`version_no` + 1); `version` = licznik edycji (CONFLICT przy złej). */
export const updateKnowledgeInput = z
  .object({
    id: idSchema,
    version: z.number().int().positive(),
    title: knowledgeTitleSchema.optional(),
    bodyMd: knowledgeBodySchema.optional(),
    url: urlSchema.nullable().optional(),
    decidedOn: localDateSchema.nullable().optional(),
  })
  .strict();
export type UpdateKnowledgeInput = z.input<typeof updateKnowledgeInput>;

export const archiveKnowledgeInput = z
  .object({ id: idSchema, version: z.number().int().positive().optional() })
  .strict();

export const knowledgeVersionInput = z.object({ id: idSchema, versionNo: z.number().int().positive() }).strict();

/** Przywrócenie starszej wersji = nowa wersja z tą treścią (historia zostaje). */
export const restoreKnowledgeVersionInput = z
  .object({ id: idSchema, versionNo: z.number().int().positive(), version: z.number().int().positive() })
  .strict();

/** Decyzje klienta albo projektu (lista „Decyzje”); bez filtra = wszystkie decyzje w zakresie osoby. */
export const listDecisionsInput = z
  .object({
    clientId: idSchema.optional(),
    projectId: idSchema.optional(),
    /** tylko decyzje, których źródłem jest ten rekord wiedzy (np. notatka z ustaleniami) */
    sourceItemId: idSchema.optional(),
    limit: z.number().int().min(1).max(500).default(200),
  })
  .strict()
  .default({});

export const listFilesInput = z
  .object({
    ownerType: fileOwnerTypeSchema.optional(),
    ownerId: idSchema.optional(),
    clientId: idSchema.optional(),
    projectId: idSchema.optional(),
  })
  .strict()
  .refine((v) => !v.ownerType === !v.ownerId, { message: 'Podaj typ i id właściciela', path: ['ownerId'] })
  .default({});

export const fileIdInput = z.object({ id: idSchema }).strict();

/**
 * Pola formularza wgrania (multipart): do czego plik należy. Klient i projekt dziedziczone z właściciela; dokument
 * klienta (`ownerType: 'client'`, `ownerId` = klient) ma opcjonalny projekt tego klienta, rodzaj (domyślnie „Inne”)
 * i krótki opis.
 */
export const fileUploadFieldsSchema = z
  .object({
    clientId: idSchema.nullable().optional(),
    projectId: idSchema.nullable().optional(),
    ownerType: fileOwnerTypeSchema.nullable().optional(),
    ownerId: idSchema.nullable().optional(),
    category: clientDocumentCategorySchema.nullable().optional(),
    description: fileDescriptionSchema.nullable().optional(),
  })
  .strict()
  .refine((v) => !v.ownerType === !v.ownerId, { message: 'Podaj typ i id właściciela', path: ['ownerId'] })
  .refine((v) => !v.category || v.ownerType === 'client', {
    message: FILE_MESSAGES.categoryOnlyClient,
    path: ['category'],
  });
export type FileUploadFields = z.infer<typeof fileUploadFieldsSchema>;

/** Dokumenty klienta (zakładka „Dokumenty”, MCP `list_client_documents`): pliki z filtrem rodzaju i projektu. */
export const listClientDocumentsInput = z
  .object({
    clientId: idSchema,
    category: clientDocumentCategorySchema.optional(),
    projectId: idSchema.optional(),
  })
  .strict();
export type ListClientDocumentsInput = z.input<typeof listClientDocumentsInput>;

export const exportClientMdInput = z.object({ clientId: idSchema }).strict();
export const exportProjectMdInput = z.object({ projectId: idSchema }).strict();

// ── DTO ─────────────────────────────────────────────────────────────────────
export interface KnowledgePersonRef {
  userId: string;
  name: string;
  initials: string;
  avatarColor: string | null;
}

export interface KnowledgeClientRef {
  id: string;
  name: string;
  color: string | null;
}

export interface KnowledgeProjectRef {
  id: string;
  name: string;
}

/** Źródło rekordu: etykieta i ścieżka SPA (`to: null` = brak strony albo źródło poza zakresem osoby). */
export interface KnowledgeSourceLink {
  type: string;
  id: string;
  label: string;
  to: string | null;
  /** false = źródło poza zakresem osoby albo już nie istnieje */
  visible: boolean;
}

export interface FileDto {
  id: string;
  filename: string;
  mime: string;
  /** „PDF”, „Word” … */
  typeLabel: string;
  sizeBytes: number;
  sha256: string;
  clientId: string | null;
  projectId: string | null;
  ownerType: FileOwnerType | null;
  ownerId: string | null;
  scanStatus: FileScanStatus;
  uploadedBy: KnowledgePersonRef | null;
  createdAt: Date;
  archived: boolean;
  /** `/api/files/<id>` */
  downloadUrl: string;
  /** rodzaj dokumentu klienta (tylko właściciel `client`) */
  category: ClientDocumentCategory | null;
  /** krótki opis (dokumenty klienta) */
  description: string | null;
}

// ── Dokumenty klienta (zakładka „Dokumenty”, DECISIONS 2026-10-17) ─────────

/** Plik klienta na liście „Pliki”: projekt (nazwa) i czy osoba może go przenieść do archiwum. */
export interface ClientDocumentFile extends FileDto {
  project: KnowledgeProjectRef | null;
  canArchive: boolean;
}

/** Usługa klienta w „Ustaleniach”: opis, warunki, okres i pula godzin; cena tylko z „Widzi stawki i kwoty”. */
export interface ClientAgreementService {
  id: string;
  name: string;
  /** `ServiceKind` z contracts/services */
  kind: string;
  /** `active` albo `paused` */
  status: string;
  /** bez „Widzi stawki i kwoty” kwoty w tekście zamienione na „[kwota ukryta]” */
  descriptionMd: string | null;
  billingTerms: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  /** pula godzin na miesiąc (sekundy) */
  hoursPoolSeconds: number | null;
  /** `BillingCycle` */
  cycle: string;
  /** tylko z „Widzi stawki i kwoty” (bez flagi pole pominięte) */
  price?: string | null;
  currency?: string;
}

export interface ClientDocumentsDto {
  clientId: string;
  clientName: string;
  files: {
    items: ClientDocumentFile[];
    /** rodzaje do wyboru przy wgraniu (bez „Widzi stawki i kwoty”: bez Umowy i Oferty) */
    categories: ClientDocumentCategory[];
    /** można wgrać plik klienta bez projektu (pełny dostęp do klienta z edycją) */
    canUploadToClient: boolean;
    /** projekty klienta, do których osoba może wgrać plik (edycja projektu) */
    uploadProjects: KnowledgeProjectRef[];
    /** projekty klienta widoczne dla osoby (filtr listy) */
    projects: KnowledgeProjectRef[];
  };
  /** odnośniki klienta (`clients.links`); null = brak pełnego dostępu do klienta (dostęp tylko przez projekt) */
  links: { items: { label: string; url: string }[]; canEdit: boolean; version: number } | null;
  agreements: {
    /** aktywne i wstrzymane usługi klienta; null = brak modułu Projekty / Utrzymanie */
    services: ClientAgreementService[] | null;
    /** decyzje klienta z Wiedzy; null = brak modułu Wiedza */
    decisions: DecisionListItem[] | null;
    /** „Dodaj ustalenie” (notatka-decyzja Wiedzy przypięta do klienta) */
    canAddDecision: boolean;
  };
}

export interface KnowledgeListItem {
  id: string;
  kind: KnowledgeKind;
  scope: KnowledgeScope;
  title: string;
  client: KnowledgeClientRef | null;
  project: KnowledgeProjectRef | null;
  /** numer bieżącej wersji treści */
  currentVersion: number;
  /** licznik edycji (do `update`, `archive`, `restoreVersion`) */
  version: number;
  decidedOn: string | null;
  url: string | null;
  file: FileDto | null;
  sourceType: KnowledgeSourceType;
  source: KnowledgeSourceLink | null;
  author: KnowledgePersonRef | null;
  createdAt: Date;
  updatedAt: Date;
  archived: boolean;
  /** początek treści bez Markdown (podgląd w liście, wynik wyszukiwania) */
  excerpt: string | null;
}

export interface KnowledgeItemDto extends KnowledgeListItem {
  bodyMd: string;
  /** autor i data bieżącej wersji */
  versionAuthor: KnowledgePersonRef | null;
  versionCreatedAt: Date;
  attachments: FileDto[];
  canEdit: boolean;
}

export interface KnowledgeVersionListItem {
  versionNo: number;
  author: KnowledgePersonRef | null;
  createdAt: Date;
  current: boolean;
  /** długość treści w znakach */
  length: number;
}

export interface KnowledgeVersionDto {
  itemId: string;
  versionNo: number;
  bodyMd: string;
  author: KnowledgePersonRef | null;
  createdAt: Date;
  current: boolean;
}

export interface DecisionListItem {
  id: string;
  title: string;
  /** data decyzji (lokalna) */
  decidedOn: string | null;
  excerpt: string | null;
  client: KnowledgeClientRef | null;
  project: KnowledgeProjectRef | null;
  source: KnowledgeSourceLink | null;
  author: KnowledgePersonRef | null;
  createdAt: Date;
}

/** Wygenerowany CLIENT.md / PROJECT.md (baza = źródło prawdy, plik tylko generowany). */
export interface MarkdownExport {
  kind: 'client' | 'project';
  id: string;
  filename: 'CLIENT.md' | 'PROJECT.md';
  markdown: string;
  generatedAt: Date;
  /** rekordy, z których powstał plik (typ, etykieta, data ostatniej zmiany) */
  sources: { type: string; id: string; label: string; updatedAt: Date | null }[];
}

/** Wynik ⌘K dla wiedzy (`knowledge.searchForGlobal`); kształt jak `SearchHit` z contracts/search. */
export interface KnowledgeSearchHit {
  type: 'knowledge';
  id: string;
  title: string;
  subtitle: string;
  to: string;
  client: KnowledgeClientRef | null;
  score: number;
}

export const KNOWLEDGE_MESSAGES = {
  archived: 'Rekord jest w archiwum. Przywróć go, aby edytować.',
  sourceMissing: 'Źródło nie istnieje albo jest poza Twoim zakresem.',
  sourceHidden: 'Źródło poza Twoim zakresem',
  fileScope: 'Plik należy do innego klienta albo projektu.',
  exportForbidden: 'Brak uprawnienia do eksportu danych.',
  sameVersion: 'To jest bieżąca wersja.',
  urlOnlyLink: 'Adres ma tylko rekord typu „Link”.',
  dateOnlyDecision: 'Datę ma tylko decyzja.',
} as const;
